import { Chip } from "@mui/material"

import { z } from 'zod'

export const BOOK_ENTRY_DEPOSIT_OPTIONS = [
    { id: 'yes', value: 'Yes', label: 'Yes' },
    { id: 'no', value: 'No', label: 'No' },
];

export const MINOR_HEAD_CHALLAN_DATA = [
    { id: '100', value: '100', label: '100' },
    { id: '200', value: '200', label: '200' },
    { id: '400', value: '400', label: '400' },
];

export const CATEGORY_OPTIONS = [
    { id: 'corporate', value: 'Corporate', label: 'Corporate' },
    { id: 'non-corporate', value: 'Non-corporate', label: 'Non-corporate', defaultValue: true },
];

// Optional numeric fields (12 digits, 2 decimals), default to "0" if empty
const numeric12_2 = z
    .string()
    .regex(/^\d{0,12}(\.\d{0,2})?$/, "Must be a number up to 12 digits and 2 decimals")
    .transform(val => (val === "" ? "0" : val));

// Mandatory numeric fields (12 digits, 2 decimals), default to "0" if empty
const mandatoryNumeric12_2 = z
    .string()
    .nonempty("This field is required")
    .regex(/^\d{0,12}(\.\d{0,2})?$/, "Must be a number up to 12 digits and 2 decimals")
    .transform(val => (val === "" ? "0" : val));

export const allocationRowSchema = z.object({
    challanSerialNo: z
        .string()
        .nonempty({ message: 'This field is required in TDS return.' })
        .regex(/^\d{1,9}$/, { message: 'Must be numeric and up to 9 digits.' }),
    bsrCode: z
        .string()
        .nonempty({ message: 'This field is required in TDS return.' })
        .regex(/^\d{7}$/, { message: 'Should contain exactly 7 digits.' }),
    dateOfTaxDeposited: z
        .string()
        .nonempty({ message: 'This field is required in TDS return.' })
        .regex(
            /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
            { message: 'Date format should be DD/MM/YYYY' }
        ),
    tdsDepositedByBookEntry: z
        .string()
        .optional()
        .default('No')
        .refine(val => ['Yes', 'No'].includes(val), { message: 'Must be Yes or No' }),
    corporateNonCorporate: z
        .string()
        .optional()
        .default('Non-corporate')
        .refine(val => ['Corporate', 'Non-corporate'].includes(val), { message: 'Must be Corporate or Non-corporate' }),
    totalTaxDeposited: mandatoryNumeric12_2,
    surcharge: numeric12_2,
    educationCess: numeric12_2,
    interest: numeric12_2,
    fee: numeric12_2,
    others: numeric12_2,
    tds: numeric12_2, // change from number to string to match other numeric fields
    sectionAsPerChallan: z
        .string()
        .nonempty({ message: 'Section as per Challan is mandatory' }),
});


const getAllocationStatusStyles = status => {
    switch (status?.toLowerCase()) {
        case 'new':
            return { backgroundColor: '#DDDDDD', color: 'var(--forbrand-secondary)' }
        case 'partial':
            return { backgroundColor: '#FFDFC4', color: '#D17300' }
        case 'full':
            return { backgroundColor: '##F0FDF3', color: '#197C37' }
        default:
            return { backgroundColor: '#e2e3e5', color: '#495057' }
    }
}

export const columns = [
    {
        id: 'allocationStatus',
        label: 'Allocation status',
        width: '200px',
        render: value => {
            return (
                <Chip
                    label={value}
                    size="small"
                    sx={{
                        ...getAllocationStatusStyles(value),
                        fontWeight: 500,
                        fontSize: '12px',
                        height: '24px',
                        borderRadius: '0',
                    }}
                />
            )
        },
    },
    {
        id: 'year',
        label: 'Year',
        width: '144px',
        isFormatted: false
    },
    {
        id: 'challanSerialNo',
        label: 'Challan serial no.',
        width: '180px',
        editable: row => row.allocationStatus === 'New',
        isFormatted: false

    },
    {
        id: 'bsrCode',
        label: 'BSR code',
        width: '144px',
        editable: row => row.allocationStatus === 'New',
        isFormatted: false
    },
    {
        id: 'dateOfTaxDeposited',
        label: 'Date of deposit',
        width: '144px',
        editable: row => row.allocationStatus === 'New',
    },
    {
        id: 'sectionAsPerChallan',
        label: 'Section as per challan',
        width: '200px',
        editable: row => row.allocationStatus === 'New',
    },
    {
        id: 'tds',
        label: 'TDS',
        width: '144px',
        editable: row => row.allocationStatus === 'New',
    },
    {
        id: 'surcharge',
        label: 'Surcharge',
        width: '144px',
        editable: row => row.allocationStatus === 'New',
    },
    {
        id: 'educationCess',
        label: 'Education cess',
        width: '144px',
        editable: row => row.allocationStatus === 'New',
    },
    {
        id: 'interest',
        label: 'Interest',
        width: '144px',
        editable: row => row.allocationStatus === 'New',
    },
    {
        id: 'fee',
        label: 'Fee',
        width: '144px',
        editable: row => row.allocationStatus === 'New',
    },
    {
        id: 'others',
        label: 'Others',
        width: '144px',
        editable: row => row.allocationStatus === 'New',
    },
    {
        id: 'totalTaxDeposited',
        label: 'Total tax deposited',
        width: '200px',
        editable: row => row.allocationStatus === 'New',
    },
    {
        id: 'tdsDepositedByBookEntry',
        label: 'Whether TDS deposited by book entry',
        width: '350px',
        editable: row => row.allocationStatus === 'New',
        type: 'dropdown',
        options: BOOK_ENTRY_DEPOSIT_OPTIONS,
        align: 'center',
    },
    {
        id: 'minorHeadOfChallan',
        label: 'Minor head of challan',
        width: '250px',
        editable: row => row.allocationStatus === 'New',
        type: 'dropdown',
        options: MINOR_HEAD_CHALLAN_DATA,
        align: 'center'
    },
    {
        id: 'corporateNonCorporate',
        label: 'Corporate/Non-corporate',
        width: '250px',
        editable: row => row.allocationStatus === 'New',
        type: 'dropdown',
        options: CATEGORY_OPTIONS
    },
    {
        id: 'allocatedAmount',
        label: 'Allocated amount',
        width: '200px',
    },
    {
        id: 'balanceAvailable',
        label: 'Balance available',
        width: '200px',
    },
]


const ddMMYYYYWithSlash = z
    .string()
    .regex(
        /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
        "Invalid format. Example: 21/10/2024 (dd/mm/yyyy)"
    );


export const searchFields = [
    ...columns.map(col => {
        if (
            col.id === 'dateOfTaxDeposited'
        ) {
            return {
                ...col,
                type: 'text',
                placeholder: `Example: 24/11/20219 (dd/mm/yyyy)`,
                zod: ddMMYYYYWithSlash,
            }
        }
        return {
            ...col,
            type: col.type || 'text',
            placeholder: `Enter ${col.label}`,
        }
    }),
]
